import type { LucideIcon } from 'lucide-react'
import { AlarmClock, CircleCheck, CircleDot, OctagonAlert } from 'lucide-react'
import { Badge } from '@/shared/ui/badge'
import { t, type TranslationKey } from '@/shared/lib/i18n'
import { cn } from '@/shared/lib/utils'
import { STATUS_PALETTE, type PaletteHue } from '@/shared/constants/statusPalette'

const PROMINENT_BASE_CLASS = 'gap-1.5 px-2.5 py-1 text-sm font-semibold'

/**
 * Styling and i18n for instrument task health codes. Unknown codes use UTD (up-to-date) styling/label.
 *
 * Colors come from STATUS_PALETTE (the single source of truth) so the badge and the
 * compliance timeline bar read as one semantic system (REBUILD-207 / §06 scheduling scale):
 * Ready/before = blue, Due Now = green, Overdue = red. Up-to-date carries no bar zone, so it
 * stays neutral (muted) to avoid clashing with the green Due Now zone.
 */

export const TASK_STATUS_BADGE_MAP: Record<
  string,
  {
    variant: 'destructive' | 'warning' | 'default' | 'secondary' | 'outline'
    className?: string
    prominentClassName?: string
    icon: LucideIcon
  }
> = {
  ODE: {
    variant: 'outline',
    className: `${STATUS_PALETTE.red.deepText} ${STATUS_PALETTE.red.border}`,
    prominentClassName: `${STATUS_PALETTE.red.tintBg} ${STATUS_PALETTE.red.deepText} ${STATUS_PALETTE.red.border}`,
    icon: OctagonAlert,
  },
  DNW: {
    variant: 'outline',
    className: `${STATUS_PALETTE.green.deepText} ${STATUS_PALETTE.green.border}`,
    prominentClassName: `${STATUS_PALETTE.green.tintBg} ${STATUS_PALETTE.green.deepText} ${STATUS_PALETTE.green.border}`,
    icon: AlarmClock,
  },
  RDY: {
    variant: 'outline',
    className: `${STATUS_PALETTE.blue.deepText} ${STATUS_PALETTE.blue.border}`,
    prominentClassName: `${STATUS_PALETTE.blue.tintBg} ${STATUS_PALETTE.blue.deepText} ${STATUS_PALETTE.blue.border}`,
    icon: CircleDot,
  },
  UTD: {
    variant: 'outline',
    className: 'text-muted-foreground border-border',
    prominentClassName: 'bg-muted text-muted-foreground border-border',
    icon: CircleCheck,
  },
}

/**
 * Compliance-timeline zone colors keyed by status code — the single source of truth shared
 * by the bar segments and the now-marker so they always agree with the status badge. Derived
 * from STATUS_PALETTE: segment = tint fill, text = deep step, marker = core (solid non-text mark).
 */
export const TASK_STATUS_ZONE_COLOR: Record<
  string,
  { segment: string; text: string; marker: string }
> = {
  RDY: zoneColorFor('blue'),
  DNW: zoneColorFor('green'),
  ODE: zoneColorFor('red'),
  UTD: { segment: 'bg-muted', text: 'text-muted-foreground', marker: 'bg-muted-foreground' },
}

function zoneColorFor(hue: PaletteHue) {
  const tokens = STATUS_PALETTE[hue]
  return { segment: tokens.tintBg, text: tokens.deepText, marker: tokens.coreBg }
}

const FALLBACK_CODE = 'UTD'

export function taskStatusBadgeProps(taskStatusCode: string) {
  return TASK_STATUS_BADGE_MAP[taskStatusCode] ?? TASK_STATUS_BADGE_MAP[FALLBACK_CODE]!
}

function taskStatusLabelKey(taskStatusCode: string): TranslationKey {
  if (Object.prototype.hasOwnProperty.call(TASK_STATUS_BADGE_MAP, taskStatusCode)) {
    return `instruments.taskStatus.${taskStatusCode}` as TranslationKey
  }
  return `instruments.taskStatus.${FALLBACK_CODE}` as TranslationKey
}

interface TaskStatusBadgeProps {
  /** Null when the instrument type has no kept maintenance tasks (legacy N/A → "-"). */
  taskStatusCode: string | null
  className?: string
  /** Larger, higher-contrast badge with a per-status icon (Instrument Tasks rows). */
  prominent?: boolean
}

export function TaskStatusBadge({
  taskStatusCode,
  className,
  prominent = false,
}: TaskStatusBadgeProps) {
  if (taskStatusCode == null || taskStatusCode === '') {
    return (
      <span className={cn('text-sm text-muted-foreground', className)}>
        {t('instruments.taskStatus.empty')}
      </span>
    )
  }

  const {
    variant,
    className: variantClass,
    prominentClassName,
    icon: StatusIcon,
  } = taskStatusBadgeProps(taskStatusCode)

  return (
    <Badge
      variant={variant}
      className={cn(
        prominent && PROMINENT_BASE_CLASS,
        variantClass,
        prominent && prominentClassName,
        className,
      )}
    >
      {prominent && <StatusIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      {t(taskStatusLabelKey(taskStatusCode))}
    </Badge>
  )
}
